import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createLeftMenuItem, getLeftMenus } from '../../../../api/managerApi';
import '../leftMenu.css';

const validatePathActionUrl = (path, action, url) => {
  const hasPath = Boolean(String(path || '').trim());
  const hasAction = Boolean(String(action || '').trim());
  const hasUrl = Boolean(String(url || '').trim());
  const filledCount = [hasPath, hasAction, hasUrl].filter(Boolean).length;

  if (filledCount === 0) {
    return 'path, url, action 중 하나는 반드시 입력해야 합니다.';
  }

  if (filledCount > 1) {
    return 'path, url, action은 동시에 입력할 수 없습니다. 하나만 입력하세요.';
  }

  return null;
};

function LeftMenuItemCreate({ menuId, onSuccess, onCancel }) {
  const navigate = useNavigate();
  const [categoryMenus, setCategoryMenus] = useState([]);
  const [loadingMenus, setLoadingMenus] = useState(true);
  const [form, setForm] = useState({
    menu_id: menuId || '',
    label: '',
    path: '',
    url: '',
    action: '',
    sort_order: 0,
    is_active: true,
  });
  const [saving, setSaving] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');

  useEffect(() => {
    setLoadingMenus(true);
    getLeftMenus()
      .then((rows) => {
        const categories = (Array.isArray(rows) ? rows : []).filter((menu) => menu.type === 'category');
        setCategoryMenus(categories);

        setForm((prev) => {
          const requestedMenuId = menuId ? String(menuId) : '';
          const hasRequested = requestedMenuId && categories.some((menu) => String(menu.menu_id) === requestedMenuId);

          if (hasRequested) {
            return { ...prev, menu_id: requestedMenuId };
          }

          if (!prev.menu_id && categories.length > 0) {
            return { ...prev, menu_id: String(categories[0].menu_id) };
          }

          return prev;
        });
      })
      .catch((err) => {
        console.error('카테고리 메뉴 조회 실패:', err);
        alert('카테고리 메뉴 조회 실패');
      })
      .finally(() => setLoadingMenus(false));
  }, [menuId]);

  const selectedMenuLabel = useMemo(() => {
    const selected = categoryMenus.find((menu) => String(menu.menu_id) === String(form.menu_id));
    return selected?.label || '-';
  }, [categoryMenus, form.menu_id]);

  const handleChange = e => {
    const { name, value } = e.target;
    setValidationMessage('');
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const pathActionError = validatePathActionUrl(form.path, form.action, form.url);
    if (pathActionError) {
      setValidationMessage(pathActionError);
      alert(pathActionError);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        menu_id: Number(form.menu_id),
        sort_order: Number(form.sort_order || 0),
        is_active: String(form.is_active) === 'true',
      };
      const created = await createLeftMenuItem(payload);
      if (onSuccess) {
        onSuccess(created);
      } else {
        navigate('/manager/left-menu');
      }
    } catch (err) {
      console.error('하위메뉴 생성 실패:', err);
      alert('하위메뉴 생성 실패');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="mgr-panel-header">
        <div>
          <div className="mgr-panel-title">하위메뉴 생성</div>
          <div className="mgr-panel-subtitle">상위 메뉴: {selectedMenuLabel}</div>
        </div>
      </div>
      <div className="mgr-panel-body">
        <form onSubmit={handleSubmit}>
          <div className="mgr-section">
            <div className="mgr-section-title">기본 정보</div>
            <div className="mgr-section-body">
              <div className="row g-2">
                <div className="col-md-5">
                  <label className="form-label">상위 메뉴</label>
                  <select
                    className="form-select form-select-sm"
                    name="menu_id"
                    value={form.menu_id}
                    onChange={handleChange}
                    required
                    disabled={loadingMenus}
                  >
                    <option value="">카테고리 메뉴 선택</option>
                    {categoryMenus.map((menu) => (
                      <option key={menu.menu_id} value={menu.menu_id}>
                        {menu.label} ({menu.menu_key})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-7">
                  <label className="form-label">라벨 (label)</label>
                  <input className="form-control form-control-sm" name="label" value={form.label} onChange={handleChange} required />
                </div>
              </div>
            </div>
          </div>

          <div className="mgr-section">
            <div className="mgr-section-title">
              경로 설정&nbsp;
              <span className="text-muted fw-normal" style={{ textTransform: 'none', letterSpacing: 0 }}>— path, url, action 중 하나만 입력</span>
            </div>
            <div className="mgr-section-body">
              <div className="row g-2">
                <div className="col-md-6">
                  <label className="form-label">path</label>
                  <input className="form-control form-control-sm" name="path" value={form.path} onChange={handleChange} placeholder="/example" />
                </div>
                <div className="col-md-6">
                  <label className="form-label">action</label>
                  <input className="form-control form-control-sm" name="action" value={form.action} onChange={handleChange} />
                </div>
                <div className="col-md-6">
                  <label className="form-label">url</label>
                  <input className="form-control form-control-sm" name="url" value={form.url} onChange={handleChange} placeholder="https://..." />
                </div>
              </div>
              {validationMessage && <div className="text-danger small mt-2">{validationMessage}</div>}
            </div>
          </div>

          <div className="mgr-section">
            <div className="mgr-section-title">기타 설정</div>
            <div className="mgr-section-body">
              <div className="row g-2">
                <div className="col-md-3">
                  <label className="form-label">정렬 순서</label>
                  <input className="form-control form-control-sm" name="sort_order" type="number" value={form.sort_order} onChange={handleChange} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">활성 상태</label>
                  <select className="form-select form-select-sm" name="is_active" value={String(form.is_active)} onChange={handleChange}>
                    <option value="true">활성</option>
                    <option value="false">비활성</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
      <div className="mgr-form-footer">
        <button className="btn btn-primary btn-sm" onClick={handleSubmit} disabled={saving}>{saving ? '저장중...' : '생성'}</button>
        <button className="btn btn-outline-secondary btn-sm" type="button" onClick={() => (onCancel ? onCancel() : navigate(-1))}>취소</button>
      </div>
    </>
  );
}

export default LeftMenuItemCreate;
